import React from 'react';
import { Box, Typography } from '@mui/material';
import { ReactComponent as AttachFileIcon } from 'src/assets/helpAction/attachment.svg';
import { BodyTextTypography, StyledLink } from './styles';
import { useTranslation } from 'react-i18next';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { useDispatch } from 'src/redux/store';
import {
  HELP_AND_SUPPORT_FILE_FORMATS,
  isFileDuplicate,
  MAX_HELP_AND_SUPPORT_FILE_SIZE,
  MAX_HELP_AND_SUPPORT_FILES
} from './utils';
import { setAlert } from 'src/redux/slices/snackbar';
import { ALERT } from 'src/types/enum';

interface UploadFileProps {
  setAttachedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  attachedFiles: File[];
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
}

const UploadFile: React.FC<UploadFileProps> = ({
  setAttachedFiles,
  attachedFiles,
  setIsUploading
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files) return;

    setIsUploading(true);

    const selectedFiles = Array.from(event.target.files);
    const currentFileCount = attachedFiles?.length;
    const availableSlots = MAX_HELP_AND_SUPPORT_FILES - currentFileCount;

    if (availableSlots <= 0) {
      dispatch(setAlert({ msg: t(T.fileSizeError), type: ALERT.WARNING }));
      setIsUploading(false);
      return;
    }

    const validFiles: File[] = [];
    const duplicateFiles: string[] = [];
    let totalCurrentSize = attachedFiles.reduce(
      (sum, file) => sum + file.size,
      0
    );

    for (const file of selectedFiles) {
      const isDuplicate = isFileDuplicate(file, attachedFiles);

      if (isDuplicate) {
        duplicateFiles.push(file.name);
        continue;
      }

      if (validFiles.length >= availableSlots) {
        dispatch(setAlert({ msg: t(T.fileSizeError), type: ALERT.WARNING }));
        break;
      }

      if (totalCurrentSize + file.size > MAX_HELP_AND_SUPPORT_FILE_SIZE) {
        dispatch(
          setAlert({
            msg: `${file.name} ${t(T.sizeExceedError)}`,
            type: ALERT.WARNING
          })
        );
        continue;
      }
      validFiles.push(file);
      totalCurrentSize += file.size;
    }

    if (duplicateFiles.length > 0) {
      dispatch(
        setAlert({
          msg: `${t(T.duplicateFileError)}`,
          type: ALERT.WARNING
        })
      );
    }

    if (validFiles.length > 0) {
      setAttachedFiles((prevFiles) => [...prevFiles, ...validFiles]);
    }
    setIsUploading(false);

    // Reset the input value to allow re-uploading the same file
    event.target.value = '';
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="start" gap={1}>
      <Typography color="text.secondary">{t(T.maxAttachFiles)}</Typography>
      <Box display="flex" alignItems="center" gap={1}>
        <StyledLink component="label">
          <Box sx={{ width: '24px', height: '24px' }}>
            <AttachFileIcon />
          </Box>
          <BodyTextTypography
            style={{ textDecorationLine: 'underline' }}
            variant="body2"
          >
            {t(T.attachFile)}
          </BodyTextTypography>
          <input
            type="file"
            hidden
            accept={HELP_AND_SUPPORT_FILE_FORMATS}
            multiple
            onChange={handleFileChange}
          />
        </StyledLink>
      </Box>
    </Box>
  );
};

export default UploadFile;
